import { Stack } from '@mantine/core';
import classNames from 'classnames/bind';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { i18nKeys, useTranslation } from 'locales';
import { ExcludedDaybooks } from 'modules/Settings/components/ExcludedDaybooks';
import { ExcludedDebtors } from 'modules/Settings/components/ExcludedDebtors';
import { useEffect } from 'react';
import type { RouteChildrenProps } from 'react-router';
import { accountActions } from 'store/account/account.actions';

import { INTEGRATIONS_ROUTE, Integration } from '../../Integrations.constants';
import { INTEGRATION_UPDATE_CALLBACK, IntegrationFormFieldsName } from '../Integration.constants';
import styleIdentifiers from '../Integration.module.scss';
import { useGetIntegrationRights } from '../Integration.utils';
import { IntegrationHeader } from '../IntegrationHeader';
import { HorusForm, type HorusFormFields, HorusFormFieldsName } from './HorusForm';

const styles = classNames.bind(styleIdentifiers);

export const Horus = ({ history: { replace } }: RouteChildrenProps) => {
  const { t } = useTranslation();

  const company = useCompanyConfiguration();
  const getIntegrationRights = useGetIntegrationRights();

  const { isActive, canActivate } = getIntegrationRights(Integration.HORUS);

  useEffect(() => {
    if (!canActivate && !isActive) {
      replace(INTEGRATIONS_ROUTE);
    }
  }, [canActivate, isActive, replace]);

  if (!company?.tp_accounting) {
    return (
      <div className={styles('loading')}>
        <div>{t(i18nKeys.LOADING)}</div>
      </div>
    );
  }

  const handleSubmit = (data: HorusFormFields) => {
    accountActions.updateHorusSettings({
      data,
      callback: INTEGRATION_UPDATE_CALLBACK,
    });
  };

  const {
    tp_accounting: {
      use_import,
      use_export,
      debtor_update,
      issue_date_limit,
      automatic_sync,
      use_payment_matching,
    },
  } = company;

  return (
    <>
      <PageTitle>
        <PageTitle.Back />
        {Integration.HORUS}
        <PageTitle.Actions>
          <IntegrationHeader name={Integration.HORUS} />
        </PageTitle.Actions>
      </PageTitle>
      <Stack>
        <Panel>
          <HorusForm
            onSubmit={handleSubmit}
            initialValues={{
              [HorusFormFieldsName.IMPORT_INTEGRATION]: use_import,
              [HorusFormFieldsName.EXPORT_INTEGRATION]: use_export,
              [HorusFormFieldsName.PAYMENT_MATCHING]: use_payment_matching,
              [IntegrationFormFieldsName.ISSUE_DATE_LIMIT]: issue_date_limit,
              [IntegrationFormFieldsName.DEBTOR_UPDATE]: debtor_update,
              [IntegrationFormFieldsName.AUTOMATIC_SYNC]: automatic_sync,
            }}
          />
        </Panel>
        <ExcludedDebtors />
        <ExcludedDaybooks />
      </Stack>
    </>
  );
};
